import React, { useEffect, useMemo } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import {
  LayoutFactory,
  PageLayoutType,
} from '../../../core/layout/LayoutFactory';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
  authTokenState,
  roleAccessState,
} from '../../../core/atoms/auth/auth.atom';
import { routes } from '../../routes/routes';
import LazyLoad from '../../../core/common/LazyLoading/LazyLoading';
import NotFoundPage from '../../../core/pages/error/NotFoundPage/NotFoundPage';
import { useMutation } from '@tanstack/react-query';
import axios, { SuccessResponse } from '../../../core/libraries/axios/axios';
import { MappedRoleAccess } from '../../../core/domain/Role';
import { DefaultError } from '@tanstack/query-core/build/modern/index';
import { apiRoutes } from '../../api/apiRoutes';
import {
  ProgressFactory,
  ProgressVariant,
} from '../../../core/common/Progress/ProgressFactory';

/* eslint-disable new-cap */
const Dashboard = LazyLoad(
  () => import('../../../core/pages/admin/DashboardPage/DashboardPage'),
);
const UsersPage = LazyLoad(
  () => import('../../../core/pages/admin/User/UsersPage/UsersPage'),
);
const UserCreatePage = LazyLoad(
  () => import('../../../core/pages/admin/User/UserCreatePage/UserCreatePage'),
);
const UserEditPage = LazyLoad(
  () => import('../../../core/pages/admin/User/UserEditPage/UserEditPage'),
);
const TranslationsPage = LazyLoad(
  () =>
    import(
      '../../../core/pages/admin/Translation/TranslationsPage/TranslationsPage'
    ),
);
const ProfilePage = LazyLoad(
  () => import('../../../core/pages/admin/Profile/ProfilePage/ProfilePage'),
);
const SettingsPage = LazyLoad(
  () => import('../../../app/pages/admin/Settings/SettingsPage/SettingsPage'),
);

const LoginPage = LazyLoad(
  () => import('../../../core/pages/public/LoginPage/LoginPage'),
);
const PasswordResetPage = LazyLoad(
  () =>
    import('../../../core/pages/public/PasswordResetPage/PasswordResetPage'),
);
const PasswordResetConfirmationPage = LazyLoad(
  () =>
    import(
      '../../../core/pages/public/PasswordResetConfirmationPage/PasswordResetConfirmationPage'
    ),
);
const RegistrationConfirmationPage = LazyLoad(
  () =>
    import(
      '../../../core/pages/public/RegistrationConfirmationPage/RegistrationConfirmationPage'
    ),
);
const DeletionConfirmationPage = LazyLoad(
  () =>
    import(
      '../../../core/pages/public/DeletionConfirmationPage/DeletionConfirmationPage'
    ),
);

const EmployeesPage = LazyLoad(
  () => import('../../../app/pages/admin/Employee/EmployeesPage/EmployeesPage'),
);
const EmployeeCreatePage = LazyLoad(
  () =>
    import(
      '../../../app/pages/admin/Employee/EmployeeCreatePage/EmployeeCreatePage'
    ),
);
const EmployeeEditPage = LazyLoad(
  () =>
    import(
      '../../../app/pages/admin/Employee/EmployeeEditPage/EmployeeEditPage'
    ),
);
const EmployeePage = LazyLoad(
  () => import('../../../app/pages/admin/Employee/EmployeePage/EmployeePage'),
);

const VehiclesPage = LazyLoad(
  () => import('../../../app/pages/admin/Vehicle/VehiclesPage/VehiclesPage'),
);
const VehicleCreatePage = LazyLoad(
  () =>
    import(
      '../../../app/pages/admin/Vehicle/VehicleCreatePage/VehicleCreatePage'
    ),
);
const VehicleEditPage = LazyLoad(
  () =>
    import('../../../app/pages/admin/Vehicle/VehicleEditPage/VehicleEditPage'),
);

const ClientsPage = LazyLoad(
  () => import('../../../app/pages/admin/Client/ClientsPage/ClientsPage'),
);
const ClientCreatePage = LazyLoad(
  () =>
    import('../../../app/pages/admin/Client/ClientCreatePage/ClientCreatePage'),
);
const ClientEditPage = LazyLoad(
  () => import('../../../app/pages/admin/Client/ClientEditPage/ClientEditPage'),
);
const ClientVehicleCreatePage = LazyLoad(
  () =>
    import(
      '../../../app/pages/admin/Client/ClientVehicleCreatePage/ClientVehicleCreatePage'
    ),
);
const ClientVehicleEditPage = LazyLoad(
  () =>
    import(
      '../../../app/pages/admin/Client/ClientVehicleEditPage/ClientVehicleEditPage'
    ),
);

const ClientVehiclesPage = LazyLoad(
  () =>
    import(
      '../../../app/pages/admin/ClientVehicle/ClientVehiclesPage/ClientVehiclesPage'
    ),
);
const GlobalClientVehicleCreatePage = LazyLoad(
  () =>
    import(
      '../../../app/pages/admin/ClientVehicle/GlobalClientVehicleCreatePage/GlobalClientVehicleCreatePage'
    ),
);
const GlobalClientVehicleEditPage = LazyLoad(
  () =>
    import(
      '../../../app/pages/admin/ClientVehicle/GlobalClientVehicleEditPage/GlobalClientVehicleEditPage'
    ),
);

const TimeOffsPage = LazyLoad(
  () => import('../../../app/pages/admin/TimeOff/TimeOffsPage/TimeOffsPage'),
);
const EmployeeTimeOffsPage = LazyLoad(
  () =>
    import(
      '../../../app/pages/admin/TimeOff/EmployeeTimeOffsPage/EmployeeTimeOffsPage'
    ),
);

const ItemsPage = LazyLoad(
  () => import('../../../app/pages/admin/Item/ItemsPage/ItemsPage'),
);

const OrdersPage = LazyLoad(
  () => import('../../../app/pages/admin/Order/OrdersPage/OrdersPage'),
);
const OrderCreatePage = LazyLoad(
  () =>
    import('../../../app/pages/admin/Order/OrderCreatePage/OrderCreatePage'),
);
const CommercialOfferCreatePage = LazyLoad(
  () =>
    import(
      '../../../app/pages/admin/CommercialOffer/CommercialOfferCreatePage/CommercialOfferCreatePage'
    ),
);
const OrderEditPage = LazyLoad(
  () => import('../../../app/pages/admin/Order/OrderEditPage/OrderEditPage'),
);
const CommercialOfferEditPage = LazyLoad(
  () =>
    import(
      '../../../app/pages/admin/CommercialOffer/CommercialOfferEditPage/CommercialOfferEditPage'
    ),
);

const TasksPage = LazyLoad(
  () => import('../../../app/pages/admin/Task/TasksPage/TasksPage'),
);
const EmployeeTasksPage = LazyLoad(
  () =>
    import('../../../app/pages/admin/Task/EmployeeTasksPage/EmployeeTasksPage'),
);

const InvoicesPage = LazyLoad(
  () => import('../../../app/pages/admin/Invoice/InvoicesPage/InvoicesPage'),
);
const InvoicePage = LazyLoad(
  () => import('../../../app/pages/admin/Invoice/InvoicePage/InvoicePage'),
);

const OrdersOverviewPage = LazyLoad(
  () =>
    import(
      '../../../app/pages/admin/Order/OrdersOverviewPage/OrdersOverviewPage'
    ),
);

const ExpensesPage = LazyLoad(
  () => import('../../../app/pages/admin/Expense/ExpensesPage/ExpensesPage'),
);

const CommercialOfferConfirmationPage = LazyLoad(
  () =>
    import(
      '../../../app/pages/public/CommercialOfferConfirmationPage/CommercialOfferConfirmationPage'
    ),
);
const CommercialOffersPage = LazyLoad(
  () =>
    import(
      '../../../app/pages/admin/CommercialOffer/CommercialOffersPage/CommercialOffersPage'
    ),
);
/* eslint-enable new-cap */

export const RouteProvider = () => {
  const authStateValue = useRecoilValue(authTokenState);
  const setRoleAccessStateValue = useSetRecoilState(roleAccessState);

  const { mutate: getMappedRoleAccess, isPending } = useMutation<
    SuccessResponse<MappedRoleAccess[]>,
    DefaultError
  >({
    mutationFn: () => axios.get(apiRoutes.roles.mappedAccess),
    onSuccess: (response) => {
      setRoleAccessStateValue(response.data);
    },
  });

  useEffect(() => {
    if (authStateValue) {
      getMappedRoleAccess();
    }
  }, [authStateValue]);

  const adminPages = useMemo(() => {
    if (authStateValue) {
      return (
        <>
          <Route path={routes.admin.dashboard} element={<Dashboard />} />
          <Route path={routes.homepage} element={<Dashboard />} />
          <Route path={routes.admin.users.list} element={<UsersPage />} />
          <Route path={routes.admin.users.new} element={<UserCreatePage />} />
          <Route path={routes.admin.users.edit} element={<UserEditPage />} />
          <Route path={routes.admin.profile} element={<ProfilePage />} />
          <Route path={routes.admin.settings} element={<SettingsPage />} />
          <Route
            path={routes.admin.translations.list}
            element={<TranslationsPage />}
          />
          <Route
            path={routes.admin.employees.list}
            element={<EmployeesPage />}
          />
          <Route
            path={routes.admin.employees.new}
            element={<EmployeeCreatePage />}
          />
          <Route
            path={routes.admin.employees.employee}
            element={<EmployeePage />}
          />
          <Route
            path={routes.admin.employees.edit}
            element={<EmployeeEditPage />}
          />
          <Route path={routes.admin.vehicles.list} element={<VehiclesPage />} />
          <Route
            path={routes.admin.vehicles.new}
            element={<VehicleCreatePage />}
          />
          <Route
            path={routes.admin.vehicles.edit}
            element={<VehicleEditPage />}
          />
          <Route path={routes.admin.clients.list} element={<ClientsPage />} />
          <Route
            path={routes.admin.clients.new}
            element={<ClientCreatePage />}
          />
          <Route
            path={routes.admin.clients.edit}
            element={<ClientEditPage />}
          />
          <Route
            path={routes.admin.clients.newClientVehicle}
            element={<ClientVehicleCreatePage />}
          />
          <Route
            path={routes.admin.clients.editClientVehicle}
            element={<ClientVehicleEditPage />}
          />
          <Route
            path={routes.admin.clientVehicles.list}
            element={<ClientVehiclesPage />}
          />
          <Route
            path={routes.admin.clientVehicles.new}
            element={<GlobalClientVehicleCreatePage />}
          />
          <Route
            path={routes.admin.clientVehicles.edit}
            element={<GlobalClientVehicleEditPage />}
          />
          <Route path={routes.admin.timeOffs.list} element={<TimeOffsPage />} />
          <Route
            path={routes.admin.timeOffs.employeeList}
            element={<EmployeeTimeOffsPage />}
          />
          <Route path={routes.admin.items.list} element={<ItemsPage />} />
          <Route path={routes.admin.orders.list} element={<OrdersPage />} />
          <Route path={routes.admin.orders.new} element={<OrderCreatePage />} />
          <Route
            path={routes.admin.commercialOffers.list}
            element={<CommercialOffersPage />}
          />
          <Route
            path={routes.admin.commercialOffers.new}
            element={<CommercialOfferCreatePage />}
          />
          <Route path={routes.admin.orders.edit} element={<OrderEditPage />} />
          <Route path={routes.admin.orders.list} element={<OrdersPage />} />
          <Route
            path={routes.admin.commercialOffers.edit}
            element={<CommercialOfferEditPage />}
          />
          <Route path={routes.admin.tasks.schedule} element={<TasksPage />} />
          <Route
            path={routes.admin.tasks.employeeSchedule}
            element={<EmployeeTasksPage />}
          />
          <Route path={routes.admin.invoices.list} element={<InvoicesPage />} />
          <Route path={routes.admin.invoices.edit} element={<InvoicePage />} />
          <Route
            path={routes.admin.orders.overview}
            element={<OrdersOverviewPage />}
          />
          <Route path={routes.admin.expenses.list} element={<ExpensesPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </>
      );
    }

    return <></>;
  }, [authStateValue]);

  const authPages = useMemo(() => {
    if (authStateValue) {
      return (
        <>
          <Route
            path={routes.auth.registrationConfirmation}
            element={<RegistrationConfirmationPage />}
          />
          <Route
            path={routes.auth.deletionConfirmation}
            element={<DeletionConfirmationPage />}
          />
        </>
      );
    }

    return (
      <>
        <Route
          path={routes.auth.registrationConfirmation}
          element={<RegistrationConfirmationPage />}
        />
        <Route
          path={routes.auth.deletionConfirmation}
          element={<DeletionConfirmationPage />}
        />
        <Route
          path={routes.auth.passwordReset}
          element={<PasswordResetPage />}
        />
        <Route
          path={routes.auth.passwordResetConfirmation}
          element={<PasswordResetConfirmationPage />}
        />
        <Route path={routes.auth.login} element={<LoginPage />} />
        <Route path={routes.homepage} element={<LoginPage />} />
        <Route path="*" element={<Navigate to={routes.auth.login} replace />} />
      </>
    );
  }, [authStateValue]);

  const publicPages = (
    <>
      <Route
        path={routes.public.commercialOfferConfirm}
        element={<CommercialOfferConfirmationPage />}
      />
    </>
  );

  if (authStateValue && isPending) {
    return <ProgressFactory variant={ProgressVariant.linear} />;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<LayoutFactory variant={PageLayoutType.admin} />}>
          {adminPages}
        </Route>
        <Route element={<LayoutFactory variant={PageLayoutType.auth} />}>
          {publicPages}
          {authPages}
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

export default RouteProvider;
